import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion, useInView } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check, Zap, Users, Rocket, Play, Volume2, VolumeX } from "lucide-react";
import { usePageTitle } from "../lib/usePageTitle";
import { API } from "../lib/api";
import PremiumBackdrop from "../components/PremiumBackdrop";

/* /build . Warm-cream light-theme variant of the video engine page. */

const BG = "#F4EFE6";
const CARD = "#FFFFFF";
const INK = "#0F1114";
const MUTED = "#5A5D66";
const ACCENT = "#E85D33";
const BORDER = "rgba(15,17,20,0.08)";

const REVENUE = [
  { value: "0_10k", label: "$0 - $10k" },
  { value: "10k_30k", label: "$10k - $30k" },
  { value: "30k_plus", label: "$30k+" },
];
const BOTTLENECK = [
  { value: "cold_leads", label: "Sourcing Cold B2B Leads" },
  { value: "warm_dropoffs", label: "Converting Warm Inbound Drop-offs" },
];

function Reveal({ children, delay = 0, className = "", testid }) {
  const reduced = useReducedMotion();
  const p = reduced
    ? {}
    : {
        initial: { opacity: 0, y: 20 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-15% 0px" },
        transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1], delay },
      };
  return (
    <motion.div className={className} data-testid={testid} {...p}>
      {children}
    </motion.div>
  );
}

/* Waveform for video mockups */
function Waveform({ bars = 20, color = "#E85D33", active = true }) {
  const reduced = useReducedMotion();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "3px", height: "22px" }}>
      {Array.from({ length: bars }).map((_, i) => (
        <motion.span
          key={i}
          style={{ width: "2px", background: color, borderRadius: "2px", display: "block" }}
          animate={reduced || !active ? { height: 6 } : { height: [4 + (i % 5) * 3, 8 + (i % 7) * 3, 4 + (i % 5) * 3] }}
          transition={{ duration: 0.8 + (i % 4) * 0.15, delay: i * 0.05, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

/* Video card mockup . warm cream variant */
function DemoCard({ demo, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-15%" });
  const reduced = useReducedMotion();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!inView || reduced) return undefined;
    const t = setInterval(() => setProgress((p) => (p + 1) % 101), 55);
    return () => clearInterval(t);
  }, [inView, reduced]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15%" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`build-demo-${demo.id}`}
      style={{
        aspectRatio: "3/4",
        borderRadius: "20px",
        background: `linear-gradient(160deg, ${demo.bg1} 0%, ${demo.bg2} 100%)`,
        border: "1px solid rgba(15,17,20,0.10)",
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 20px 40px -18px rgba(15,17,20,0.20)",
      }}
    >
      {/* Grid overlay */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, opacity: 0.20,
        backgroundImage: "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
        backgroundSize: "20px 20px",
      }} />

      {/* Live badge top-left */}
      <div style={{
        position: "absolute", top: "14px", left: "14px",
        display: "inline-flex", alignItems: "center", gap: "6px",
        background: "rgba(255,255,255,0.85)", backdropFilter: "blur(6px)",
        padding: "4px 10px", borderRadius: "999px",
        fontSize: "10px", color: "#141518", fontWeight: 600, letterSpacing: "0.06em",
      }}>
        <motion.span
          style={{ height: "6px", width: "6px", borderRadius: "50%", background: "#E85D33" }}
          animate={reduced ? {} : { opacity: [1, 0.3, 1] }}
          transition={{ duration: 1.2, repeat: Infinity }}
        />
        LIVE
      </div>

      {/* Duration */}
      <div style={{
        position: "absolute", top: "14px", right: "14px",
        background: "rgba(255,255,255,0.85)", backdropFilter: "blur(6px)",
        padding: "4px 10px", borderRadius: "999px",
        fontSize: "11px", color: "#141518", fontFamily: "monospace",
      }}>
        {demo.duration}
      </div>

      {/* Center avatar bubble */}
      <div style={{
        position: "absolute", top: "28%", left: "50%", transform: "translateX(-50%)",
        display: "flex", flexDirection: "column", alignItems: "center", gap: "14px",
      }}>
        <div style={{ position: "relative" }}>
          <div style={{
            height: "76px", width: "76px", borderRadius: "50%",
            background: "#FFFFFF", border: "3px solid #FFFFFF",
            boxShadow: "0 12px 24px -8px rgba(0,0,0,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: demo.bg2, fontSize: "26px", fontWeight: 700, letterSpacing: "-0.02em",
          }}>
            {demo.initials}
          </div>
          <motion.span
            aria-hidden
            initial={{ scale: 1, opacity: 0.6 }}
            animate={reduced ? {} : { scale: [1, 1.4], opacity: [0.6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
            style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "2px solid #FFFFFF" }}
          />
          <div style={{
            position: "absolute", bottom: "-4px", right: "-4px",
            height: "26px", width: "26px", borderRadius: "50%",
            background: "#E85D33", border: "2px solid #FFFFFF",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Play className="h-3 w-3" style={{ color: "#FFFFFF", marginLeft: 1 }} fill="currentColor" />
          </div>
        </div>
        <div style={{
          background: "rgba(255,255,255,0.90)", padding: "8px 14px", borderRadius: "999px",
          fontSize: "12px", color: "#141518", fontWeight: 500,
          boxShadow: "0 6px 12px -6px rgba(0,0,0,0.15)",
        }}>
          Hi {demo.firstName},
        </div>
        <Waveform bars={20} color="#FFFFFF" active={inView} />
      </div>

      {/* Progress bar */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "3px", background: "rgba(0,0,0,0.15)" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: "#FFFFFF", transition: "width 55ms linear" }} />
      </div>

      {/* Caption */}
      <div style={{
        position: "absolute", bottom: "16px", left: "14px", right: "14px",
        padding: "12px 14px", borderRadius: "12px",
        background: "rgba(255,255,255,0.94)", backdropFilter: "blur(8px)",
      }}>
        <p style={{ fontSize: "9px", letterSpacing: "0.14em", color: "#E85D33", textTransform: "uppercase", fontWeight: 700 }}>
          Sent to
        </p>
        <p style={{ marginTop: "3px", fontSize: "13px", color: "#141518", fontWeight: 600 }}>{demo.prospect}</p>
        <p style={{ marginTop: "2px", fontSize: "10px", color: "#5F6169" }}>{demo.title}</p>
      </div>
    </motion.div>
  );
}

const DEMOS = [
  { id: "priya", initials: "PS", firstName: "Priya", prospect: "Priya Sharma", title: "VP Growth . Hospitality", duration: "0:47", bg1: "#F4A75E", bg2: "#E85D33" },
  { id: "james", initials: "JN", firstName: "James", prospect: "James Northcott", title: "Founder . Consultancy", duration: "0:52", bg1: "#7BA6C4", bg2: "#3A5F82" },
  { id: "leila", initials: "LO", firstName: "Leila", prospect: "Leila Okafor", title: "Head of Ops . Clinics", duration: "0:41", bg1: "#8FAF7E", bg2: "#4E6E48" },
];

const btnPrimary = {
  padding: "14px 26px",
  borderRadius: "8px",
  fontSize: "15px",
  fontWeight: 600,
  background: INK,
  color: "#FFFFFF",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  border: "none",
  cursor: "pointer",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
};

const inputStyle = {
  background: CARD,
  border: `1px solid ${BORDER}`,
  padding: "12px 16px",
  borderRadius: "8px",
  fontSize: "15px",
  color: INK,
  width: "100%",
  outline: "none",
};

const PILLARS = [
  {
    Icon: Users,
    title: "Sourcing",
    body: "Verified B2B lead lists mined for your exact ICP. Refreshed weekly, hand-checked.",
  },
  {
    Icon: Zap,
    title: "Twin",
    body: "Your face and voice cloned into a high-definition avatar. Every video looks recorded live.",
  },
  {
    Icon: Rocket,
    title: "Launch",
    body: "Programmatic drip sends personalised video pitches straight to target inboxes.",
  },
];

const FAQ = [
  { q: "How is this different from an SDR agency?", a: "You own the engine. Digital twin, verified list, dashboard. Founder-led by Krishna, no junior handoff." },
  { q: "How fast until meetings land?", a: "First conversations by week two. First qualified meetings by week four. Eight to twelve per month by day sixty." },
  { q: "Do prospects know it is a twin?", a: "The video is trained on your face and voice, so it is genuinely yours. We disclose the setup on the follow-up call." },
  { q: "What is the pricing?", a: "$10,000 per month. First ninety days is proof. Cancel any time after day 90." },
];

export default function Build() {
  usePageTitle("Build your Video Engine");
  const navigate = useNavigate();
  const [f, setF] = useState({ full_name: "", email: "", website: "", revenue_scale: "", funnel_bottleneck: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!f.full_name.trim() || !f.email.trim() || !f.revenue_scale || !f.funnel_bottleneck) {
      toast.error("Please complete every required field.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/home-intake`, f);
      toast.success("Saved. Taking you to the scheduling brief.");
      navigate(data?.next || "/strategy-brief");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Please check your entries." : d || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const scroll = () => document.getElementById("build-form")?.scrollIntoView({ behavior: "smooth" });

  return (
    <main
      style={{
        background: BG,
        color: INK,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
      }}
      data-testid="build-page"
    >
      <PremiumBackdrop variant="light" />
      {/* HERO */}
      <section style={{ padding: "140px 24px 60px" }} data-testid="build-hero">
        <div className="max-w-[1100px] mx-auto text-center">
          <Reveal>
            <p
              className="inline-block"
              style={{
                fontSize: "13px",
                fontWeight: 500,
                color: ACCENT,
                background: "rgba(232,93,51,0.10)",
                padding: "6px 14px",
                borderRadius: "999px",
                letterSpacing: "0.02em",
              }}
              data-testid="build-hero-badge"
            >
              Managed video acquisition. By Get Virtual Growth.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1
              className="mt-8 mx-auto"
              style={{
                fontSize: "clamp(40px, 6.8vw, 76px)",
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
                fontWeight: 700,
                color: INK,
                maxWidth: "900px",
              }}
              data-testid="build-hero-title"
            >
              Build a video engine that <span style={{ color: ACCENT }}>books meetings for you</span>.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              className="mt-6 mx-auto"
              style={{ fontSize: "18px", lineHeight: 1.55, color: MUTED, maxWidth: "620px" }}
              data-testid="build-hero-sub"
            >
              Your digital twin sends personalised video pitches to a thousand ideal decision-makers a month, on autopilot. You review one weekly digest and close what lands.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button onClick={scroll} data-testid="build-hero-cta" style={btnPrimary}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
              >
                Start free intake <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="#build-pricing"
                data-testid="build-hero-secondary"
                style={{ ...btnPrimary, background: "transparent", color: INK, border: `1px solid ${BORDER}` }}
              >
                See pricing
              </a>
            </div>
          </Reveal>

          {/* Hero visual . video preview + stats side by side */}
          <Reveal delay={0.25}>
            <div
              className="mt-16 mx-auto grid md:grid-cols-[1fr_1.2fr] gap-5"
              style={{ maxWidth: "1000px" }}
              data-testid="build-hero-visual"
            >
              {/* LEFT . Hero video preview mockup */}
              <div style={{
                aspectRatio: "3/4",
                borderRadius: "24px",
                background: `linear-gradient(160deg, #F4A75E 0%, #E85D33 100%)`,
                border: `1px solid ${BORDER}`,
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 40px 80px -30px rgba(15,17,20,0.20)",
              }} data-testid="build-hero-video">
                <div aria-hidden style={{
                  position: "absolute", inset: 0, opacity: 0.18,
                  backgroundImage: "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
                  backgroundSize: "22px 22px",
                }} />
                {/* Live badge */}
                <div style={{
                  position: "absolute", top: "18px", left: "18px",
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  background: "rgba(255,255,255,0.90)", padding: "5px 12px",
                  borderRadius: "999px", fontSize: "11px", color: INK, fontWeight: 700, letterSpacing: "0.06em",
                }}>
                  <motion.span
                    style={{ height: "6px", width: "6px", borderRadius: "50%", background: "#E85D33" }}
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1.2, repeat: Infinity }}
                  />
                  LIVE
                </div>
                <div style={{
                  position: "absolute", top: "18px", right: "18px",
                  background: "rgba(255,255,255,0.90)", padding: "5px 12px",
                  borderRadius: "999px", fontSize: "12px", color: INK, fontFamily: "monospace",
                }}>0:47</div>
                {/* Big centered avatar */}
                <div style={{
                  position: "absolute", inset: 0, display: "flex",
                  flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px",
                }}>
                  <div style={{ position: "relative" }}>
                    <div style={{
                      height: "108px", width: "108px", borderRadius: "50%",
                      background: "#FFFFFF", boxShadow: "0 20px 40px -12px rgba(0,0,0,0.30)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#E85D33", fontSize: "40px", fontWeight: 700, letterSpacing: "-0.02em",
                    }}>PS</div>
                    <motion.span
                      aria-hidden
                      animate={{ scale: [1, 1.5], opacity: [0.6, 0] }}
                      transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
                      style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "2px solid #FFFFFF" }}
                    />
                    <div style={{
                      position: "absolute", bottom: "-4px", right: "-4px",
                      height: "34px", width: "34px", borderRadius: "50%",
                      background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 8px 16px -4px rgba(0,0,0,0.25)",
                    }}>
                      <Play className="h-4 w-4" style={{ color: "#E85D33", marginLeft: 2 }} fill="currentColor" />
                    </div>
                  </div>
                  <div style={{
                    background: "rgba(255,255,255,0.95)", padding: "10px 18px", borderRadius: "999px",
                    fontSize: "14px", color: INK, fontWeight: 500,
                  }}>
                    Hi Priya, I noticed you...
                  </div>
                  <Waveform bars={26} color="#FFFFFF" />
                </div>
                {/* Progress bar */}
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "4px", background: "rgba(0,0,0,0.15)" }}>
                  <motion.div
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    style={{ height: "100%", background: "#FFFFFF" }}
                  />
                </div>
              </div>

              {/* RIGHT . Stats card */}
              <div style={{
                background: CARD,
                borderRadius: "24px",
                border: `1px solid ${BORDER}`,
                padding: "24px",
                display: "flex", flexDirection: "column", justifyContent: "space-between", gap: "16px",
                boxShadow: "0 40px 80px -30px rgba(15,17,20,0.15)",
              }}>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Prospects sourced", value: "1,024" },
                    { label: "Videos rendered", value: "1,024" },
                    { label: "Meetings booked", value: "37" },
                  ].map((s) => (
                    <div key={s.label} style={{ background: BG, borderRadius: "14px", padding: "16px" }}>
                      <p style={{ fontSize: "10px", color: MUTED, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>
                        {s.label}
                      </p>
                      <p style={{ fontSize: "clamp(20px, 2.4vw, 30px)", fontWeight: 700, marginTop: "6px", letterSpacing: "-0.02em", color: INK }}>{s.value}</p>
                    </div>
                  ))}
                </div>
                <div className="space-y-2 text-left">
                  {[
                    { r: "priya@f&b-hotelgroup.ae", act: "watched 47s" },
                    { r: "james@northstar-consult.co.uk", act: "replied" },
                    { r: "leila@brava-clinics.com", act: "booked meeting" },
                  ].map((r, i) => (
                    <div key={i} style={{
                      background: BG, borderRadius: "10px", padding: "10px 14px",
                      display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px",
                      fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", color: INK,
                    }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.r}</span>
                      <span style={{
                        color: ACCENT, fontWeight: 700, fontSize: "10px",
                        background: "rgba(232,93,51,0.10)", padding: "3px 8px", borderRadius: "999px",
                        letterSpacing: "0.04em", flexShrink: 0,
                      }}>{r.act.toUpperCase()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section style={{ padding: "80px 24px" }} data-testid="build-pillars">
        <div className="max-w-[1100px] mx-auto">
          <Reveal>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15, maxWidth: "700px" }}>
              Three parts. <span style={{ color: ACCENT }}>One engine.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={0.05 * i} testid={`build-pillar-${i}`}>
                <div style={{ background: CARD, borderRadius: "16px", border: `1px solid ${BORDER}`, padding: "28px", height: "100%" }}>
                  <div style={{
                    height: "44px", width: "44px", borderRadius: "12px",
                    background: "rgba(232,93,51,0.10)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    marginBottom: "20px"
                  }}>
                    <p.Icon className="h-5 w-5" style={{ color: ACCENT }} />
                  </div>
                  <h3 style={{ fontSize: "20px", fontWeight: 600, letterSpacing: "-0.01em" }}>{p.title}</h3>
                  <p style={{ marginTop: "8px", fontSize: "15px", lineHeight: 1.55, color: MUTED }}>{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SEE IT IN ACTION . 3 demo cards */}
      <section style={{ padding: "80px 24px" }} data-testid="build-in-action">
        <div className="max-w-[1100px] mx-auto">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
              <div>
                <p style={{ fontSize: "12px", color: ACCENT, letterSpacing: "0.12em", fontWeight: 600, textTransform: "uppercase" }}>
                  See it in action
                </p>
                <h2 style={{
                  marginTop: "12px", fontSize: "clamp(28px, 4vw, 44px)",
                  fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15, maxWidth: "640px",
                }}>
                  Every prospect gets <span style={{ color: ACCENT }}>their own video</span>.
                </h2>
              </div>
              <p style={{ fontSize: "14px", color: MUTED, maxWidth: "280px", lineHeight: 1.55 }}>
                Same face. Same voice. Different first name, different opener, different landing page in the background.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {DEMOS.map((d, i) => (
              <DemoCard key={d.id} demo={d} index={i} />
            ))}
          </div>
          <Reveal delay={0.2}>
            <p style={{ marginTop: "24px", fontSize: "12px", color: MUTED, textAlign: "center" }}>
              Autoplays on scroll. 3 of 1,024 sent this month.
            </p>
          </Reveal>
        </div>
      </section>

      {/* NUMBER BLOCK */}
      <section style={{ padding: "80px 24px" }} data-testid="build-numbers">
        <div className="max-w-[900px] mx-auto text-center">
          <Reveal>
            <p style={{ fontSize: "12px", color: ACCENT, letterSpacing: "0.12em", fontWeight: 600, textTransform: "uppercase" }}>
              Programmatic velocity
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 style={{ marginTop: "20px", fontSize: "clamp(60px, 12vw, 128px)", fontWeight: 700, letterSpacing: "-0.04em", lineHeight: 0.95 }} data-testid="build-number">
              12,000
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p style={{ marginTop: "16px", fontSize: "18px", color: MUTED, lineHeight: 1.55, maxWidth: "560px", margin: "16px auto 0" }}>
              Warm touchpoints a year. Every one of them addressed by name, with a face they can trust, without you touching the camera.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PRICING */}
      <section id="build-pricing" style={{ padding: "80px 24px" }} data-testid="build-pricing">
        <div className="max-w-[720px] mx-auto text-center">
          <Reveal>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 700, letterSpacing: "-0.02em" }}>
              One tier. <span style={{ color: ACCENT }}>Nothing hidden.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div
              style={{
                marginTop: "40px",
                background: CARD,
                borderRadius: "24px",
                border: `2px solid ${INK}`,
                padding: "40px",
                textAlign: "left",
                boxShadow: "0 40px 80px -30px rgba(15,17,20,0.20)",
              }}
              data-testid="build-price-card"
            >
              <p style={{ fontSize: "12px", color: ACCENT, letterSpacing: "0.1em", fontWeight: 600, textTransform: "uppercase" }}>Managed video engine</p>
              <p style={{ marginTop: "16px", fontSize: "clamp(48px, 8vw, 68px)", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1, whiteSpace: "nowrap" }}>
                $10,000<span style={{ fontSize: "20px", color: MUTED, fontWeight: 500, marginLeft: "10px" }}>/ month</span>
              </p>
              <div style={{ marginTop: "24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                {[
                  "Digital twin built and trained",
                  "1,000 prospects sourced monthly",
                  "Full send infrastructure managed",
                  "Weekly written digest",
                  "Founder-led by Krishna",
                  "First 90 days is proof",
                ].map((line) => (
                  <div key={line} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                    <Check className="h-4 w-4" style={{ color: ACCENT, marginTop: "3px", flexShrink: 0 }} />
                    <p style={{ fontSize: "14px", lineHeight: 1.5, color: INK }}>{line}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: "32px", display: "flex", justifyContent: "center" }}>
                <button onClick={scroll} data-testid="build-price-cta" style={btnPrimary}>
                  Start free intake <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: "80px 24px" }} data-testid="build-faq">
        <div className="max-w-[720px] mx-auto">
          <Reveal>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 700, letterSpacing: "-0.02em", textAlign: "center" }}>
              Common questions.
            </h2>
          </Reveal>
          <div style={{ marginTop: "40px", display: "flex", flexDirection: "column", gap: "10px" }}>
            {FAQ.map((f, i) => (
              <Reveal key={f.q} delay={0.04 * i} testid={`build-faq-${i}`}>
                <details style={{ background: CARD, borderRadius: "14px", border: `1px solid ${BORDER}` }}>
                  <summary style={{ cursor: "pointer", padding: "20px 24px", fontSize: "17px", fontWeight: 500, listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {f.q}
                    <span style={{ color: ACCENT, fontSize: "20px", fontWeight: 300 }}>+</span>
                  </summary>
                  <p style={{ padding: "0 24px 20px", fontSize: "15px", color: MUTED, lineHeight: 1.6 }}>{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INTAKE FORM */}
      <section id="build-form" style={{ padding: "80px 24px 160px" }} data-testid="build-intake">
        <div className="max-w-[560px] mx-auto">
          <Reveal>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 700, letterSpacing: "-0.02em", textAlign: "center" }}>
              Tell us where <span style={{ color: ACCENT }}>the funnel breaks</span>.
            </h2>
            <p style={{ marginTop: "12px", textAlign: "center", fontSize: "16px", color: MUTED, lineHeight: 1.6 }}>
              Five fields. We come back inside one business day with a scoped build proposal.
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <form
              onSubmit={submit}
              style={{ marginTop: "40px", background: CARD, borderRadius: "20px", border: `1px solid ${BORDER}`, padding: "32px", display: "flex", flexDirection: "column", gap: "16px" }}
              data-testid="build-form"
            >
              {[
                { key: "full_name", label: "Full Name", type: "text", ph: "Alex Morgan", required: true },
                { key: "email", label: "Work Email", type: "email", ph: "alex@company.com", required: true },
                { key: "website", label: "Company Website", type: "url", ph: "https://company.com" },
              ].map((fld) => (
                <label key={fld.key} style={{ display: "block" }}>
                  <span style={{ display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: 500, color: INK }}>
                    {fld.label}{fld.required && <span style={{ color: ACCENT, marginLeft: 4 }}>*</span>}
                  </span>
                  <input
                    type={fld.type}
                    required={fld.required}
                    value={f[fld.key]}
                    onChange={(e) => set(fld.key, e.target.value)}
                    placeholder={fld.ph}
                    data-testid={`build-${fld.key.replace("_","-")}`}
                    style={inputStyle}
                  />
                </label>
              ))}
              <label>
                <span style={{ display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: 500, color: INK }}>
                  Monthly Revenue Scale <span style={{ color: ACCENT }}>*</span>
                </span>
                <select required value={f.revenue_scale} onChange={(e) => set("revenue_scale", e.target.value)} data-testid="build-revenue" style={inputStyle}>
                  <option value="">Select range</option>
                  {REVENUE.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <label>
                <span style={{ display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: 500, color: INK }}>
                  Primary Funnel Bottleneck <span style={{ color: ACCENT }}>*</span>
                </span>
                <select required value={f.funnel_bottleneck} onChange={(e) => set("funnel_bottleneck", e.target.value)} data-testid="build-bottleneck" style={inputStyle}>
                  <option value="">Select bottleneck</option>
                  {BOTTLENECK.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <button type="submit" disabled={busy} data-testid="build-submit" style={{ ...btnPrimary, background: ACCENT, marginTop: "8px", justifyContent: "center" }}>
                {busy ? "Saving..." : "Secure Your Video Pipeline Build"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
